"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  type TooltipContentProps,
  type PieLabelRenderProps,
} from "recharts";
import { BillingService } from "@/services/BillingService";
import type { BillingProductsOutput } from "@/interfaces/billing";
import { ChartColors } from "@/util/chartColors";
import { Package, Search, ShoppingCart } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TopProductsChartProps } from "@/interfaces/dashboard";

export const TopProductsChart: React.FC<TopProductsChartProps> = ({
  monthYear,
  limit = 5,
  isLoading = false,
}) => {
  const [products, setProducts] = useState<BillingProductsOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"chart" | "list">("chart");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<BillingProductsOutput | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new BillingService();
        const response = await service.getProducts({ monthYear });

        const sortedProducts = [...response].sort(
          (a, b) => b.quantity - a.quantity
        );

        setProducts(sortedProducts);
      } catch (err) {
        console.error("Failed to fetch products data:", err);
        setError("Falha ao carregar dados dos produtos");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [monthYear]);

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: PieLabelRenderProps) => {
    const inner = Number(innerRadius ?? 0);
    const outer = Number(outerRadius ?? inner);
    const radius = inner + (outer - inner) * 0.5;
    const angle = -Number(midAngle ?? 0) * (Math.PI / 180);
    const labelCx = Number(cx ?? 0) + radius * Math.cos(angle);
    const labelCy = Number(cy ?? 0) + radius * Math.sin(angle);
    const percentage = Math.round(Number(percent ?? 0) * 100);

    return (
      <text
        x={labelCx}
        y={labelCy}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${percentage}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const data = payload[0].payload as BillingProductsOutput;
    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-bold">{data.productName}</p>
        <p>Quantidade: {data.quantity}</p>
      </div>
    );
  };

  const filteredProducts = products.filter((product) =>
    product.productName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayProducts =
    viewMode === "chart" ? filteredProducts.slice(0, limit) : filteredProducts;

  const pieChartData = displayProducts as unknown as Record<string, unknown>[];

  const handleProductClick = (product: BillingProductsOutput) => {
    setSelectedProduct(product === selectedProduct ? null : product);
  };

  if (isLoading || loading) {
    return (
      <Card>
        <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Produtos Mais Vendidos</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <Skeleton className="h-[300px] w-full rounded-md" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Produtos Mais Vendidos</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px] text-red-500">
          {error}
        </CardContent>
      </Card>
    );
  }

  if (products.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Produtos Mais Vendidos</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px] text-muted-foreground">
          Nenhum dado disponível
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <CardTitle className="flex items-center">
          <ShoppingCart className="h-5 w-5 mr-2 text-orange-500" />
          Produtos Mais Vendidos
        </CardTitle>
        <div className="flex space-x-1">
          <Button
            variant={viewMode === "chart" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("chart")}
            className={
              viewMode === "chart" ? "bg-orange-500 hover:bg-orange-600" : ""
            }
          >
            Gráfico
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className={
              viewMode === "list" ? "bg-orange-500 hover:bg-orange-600" : ""
            }
          >
            Lista
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {viewMode === "list" && (
          <div className="mb-4 relative">
            <Input
              placeholder="Buscar produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          </div>
        )}

        <AnimatePresence mode="wait">
          {viewMode === "chart" ? (
            <motion.div
              key="chart"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="quantity"
                    nameKey="productName"
                    animationDuration={800}
                    animationBegin={0}
                  >
                    {displayProducts.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={ChartColors[index % ChartColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={CustomTooltip} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: "20px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-[300px] overflow-auto pr-2"
            >
              <div className="space-y-2">
                {displayProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[250px] text-muted-foreground">
                    <Package className="h-12 w-12 mb-2 text-muted-foreground/50" />
                    <p>Nenhum produto encontrado</p>
                  </div>
                ) : (
                  displayProducts.map((product, index) => (
                    <motion.div
                      key={product.productId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      onClick={() => handleProductClick(product)}
                      className={`p-3 border rounded-md cursor-pointer transition-all ${
                        selectedProduct?.productId === product.productId
                          ? "border-orange-500 bg-orange-50"
                          : "hover:border-gray-300"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{
                              backgroundColor:
                                ChartColors[index % ChartColors.length],
                            }}
                          />
                          <span className="font-medium">
                            {product.productName}
                          </span>
                        </div>
                        <Badge variant="outline">{product.quantity} un</Badge>
                      </div>

                      {selectedProduct?.productId === product.productId && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-2 pt-2 border-t text-sm"
                        >
                          <div className="grid grid-cols-1 gap-2">
                            <div>
                              <p className="text-muted-foreground">
                                ID do Produto:
                              </p>
                              <p className="font-semibold">
                                {product.productId}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};
